import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useEpisodeStore } from '@/state/episodeStore';
import { useSelectionStore } from '@/state/selectionStore';
import { openChannel, closeChannel } from '@/services/broadcast';
import { loadEpisodeBundle, EpisodeAccessError } from '@/services/episodeApi';
import { rememberEpisode } from '@/services/lastEpisode';
import { SceneGrid } from '@/components/SceneGrid';
import { GridToolbar } from '@/components/GridToolbar';
import { RightRail } from '@/components/rail/RightRail';
import { BottomInput } from '@/components/BottomInput';
import { isDemoMode, buildDemoBundle } from '@/dev/demoSeed';
import { useSelectionUndo } from '@/hooks/useSelectionUndo';
import { useBroadcastSync } from '@/hooks/useBroadcastSync';
import { NotFoundPage } from './NotFoundPage';

export function EpisodePage() {
  const { uuid } = useParams<{ uuid: string }>();
  const setBundle = useEpisodeStore((s) => s.setBundle);
  const setLoading = useEpisodeStore((s) => s.setLoading);
  const setLoadError = useEpisodeStore((s) => s.setLoadError);
  const reset = useEpisodeStore((s) => s.reset);
  const isLoading = useEpisodeStore((s) => s.isLoading);
  const loadError = useEpisodeStore((s) => s.loadError);
  const scenes = useEpisodeStore((s) => s.scenes);

  const activateScene = useSelectionStore((s) => s.activateScene);

  useSelectionUndo();
  useBroadcastSync();

  useEffect(() => {
    if (!uuid) return undefined;
    rememberEpisode(uuid);
    let cancelled = false;

    setLoading(true);
    setLoadError(null);

    if (isDemoMode()) {
      setBundle(buildDemoBundle(uuid));
      openChannel(uuid);
      return () => {
        closeChannel();
        reset();
        activateScene(null);
      };
    }

    loadEpisodeBundle(uuid)
      .then((bundle) => {
        if (cancelled) return;
        setBundle(bundle);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof EpisodeAccessError) {
          setLoadError('forbidden');
        } else {
          setLoadError(err instanceof Error ? err.message : 'Failed to load episode');
        }
        setLoading(false);
      });

    openChannel(uuid);
    return () => {
      cancelled = true;
      closeChannel();
      activateScene(null);
      // Don't reset() here — react-router unmounts EpisodePage when navigating
      // to /episode/X/scene/Y, but we're still inside the same episode and
      // ScenePage needs the bundle in the store. The next episode load
      // overwrites the bundle anyway, so leaving it stale is fine.
    };
  }, [uuid, setBundle, setLoading, setLoadError, reset, activateScene]);

  if (loadError === 'forbidden') {
    return <NotFoundPage kind="403" />;
  }

  return (
    <div className="flex h-full">
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <GridToolbar scenes={scenes} episodeUuid={uuid!} />
          {isLoading && scenes.length === 0 ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <p className="font-mono text-[12px] text-text-4">loading…</p>
            </div>
          ) : loadError ? (
            <div className="flex min-h-[40vh] flex-col items-center justify-center text-center">
              <p className="font-mono text-[12px] text-err">load failed</p>
              <p className="mt-2 max-w-md text-[14px] text-text-3">{loadError}</p>
            </div>
          ) : (
            <SceneGrid scenes={scenes} episodeUuid={uuid!} />
          )}
        </div>
        <BottomInput episodeUuid={uuid!} />
      </main>
      <div className="hidden w-[320px] shrink-0 lg:block xl:w-[360px]">
        <RightRail />
      </div>
    </div>
  );
}
